import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Skeleton } from '../ui/skeleton';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { financeApi, guestsApi } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { format, subDays } from 'date-fns';
import { 
  DollarSign, FileText, CreditCard, TrendingUp, Building2,
  Plus, Send, CheckCircle, AlertCircle, Receipt, PieChart
} from 'lucide-react';
import { cn } from '../../lib/utils';

export default function FinancePage() {
  const { currentProperty } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [invoices, setInvoices] = useState([]);
  const [arSummary, setArSummary] = useState([]);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);

  const fetchDashboard = useCallback(async () => {
    if (!currentProperty) return;
    try {
      setLoading(true);
      const response = await financeApi.getDashboard(currentProperty.id);
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [currentProperty]);

  const fetchInvoices = async () => {
    try {
      const response = await financeApi.listInvoices(currentProperty.id);
      setInvoices(response.data || []);
    } catch (error) {
      toast.error('Failed to load invoices');
    }
  };

  const fetchARSummary = async () => {
    try {
      const response = await financeApi.getARSummary(currentProperty.id);
      setArSummary(response.data || []);
    } catch (error) {
      console.error('Failed to load AR summary:', error);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (activeTab === 'invoices') {
      fetchInvoices();
    } else if (activeTab === 'ar') {
      fetchARSummary();
    }
  }, [activeTab, currentProperty]);

  const handleIssueInvoice = async (invoiceId) => {
    try {
      await financeApi.issueInvoice(invoiceId);
      toast.success('Invoice issued');
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue invoice');
    }
  };

  const handleRecordPayment = async (paymentData) => {
    try {
      await financeApi.recordPayment(selectedInvoice.id, currentProperty.id, paymentData);
      toast.success('Payment recorded');
      setPaymentDialogOpen(false);
      setSelectedInvoice(null);
      fetchInvoices();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      DRAFT: { variant: 'secondary' },
      ISSUED: { variant: 'default', className: 'bg-blue-500' },
      SENT: { variant: 'default', className: 'bg-indigo-500' },
      PARTIAL: { variant: 'default', className: 'bg-amber-500' },
      PAID: { variant: 'default', className: 'bg-green-500' },
      OVERDUE: { variant: 'destructive' },
      CANCELLED: { variant: 'outline' },
    };
    const c = config[status] || { variant: 'secondary' };
    return <Badge variant={c.variant} className={c.className}>{status}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="finance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Finance</h1>
          <p className="text-muted-foreground">Invoices, AR, and revenue for {currentProperty?.name}</p>
        </div>
        <Button onClick={() => setInvoiceDialogOpen(true)} data-testid="new-invoice-btn">
          <Plus className="h-4 w-4 mr-2" /> New Invoice
        </Button>
      </div>

      {/* Dashboard Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">ETB {(dashboard?.today?.charges || 0).toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Today&apos;s Charges</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CreditCard className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">ETB {(dashboard?.today?.payments || 0).toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Today&apos;s Payments</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <FileText className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{dashboard?.ar?.open_invoices || 0}</p>
                <p className="text-xs text-muted-foreground">Open Invoices</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Building2 className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">ETB {(dashboard?.ar?.total_balance || 0).toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">AR Balance</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payment Breakdown */}
      {dashboard?.today && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Today&apos;s Collections</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-green-700">ETB {(dashboard.today.cash_collected || 0).toLocaleString()}</div>
                <div className="text-sm text-green-600">Cash</div>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-700">ETB {(dashboard.today.card_collected || 0).toLocaleString()}</div>
                <div className="text-sm text-blue-600">Card</div>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-purple-700">ETB {(dashboard.today.other_collected || 0).toLocaleString()}</div>
                <div className="text-sm text-purple-600">Other</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">
            <PieChart className="h-4 w-4 mr-2" /> Overview
          </TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">
            <FileText className="h-4 w-4 mr-2" /> Invoices
          </TabsTrigger>
          <TabsTrigger value="ar" data-testid="tab-ar">
            <Building2 className="h-4 w-4 mr-2" /> Accounts Receivable
          </TabsTrigger>
          <TabsTrigger value="revenue" data-testid="tab-revenue">
            <TrendingUp className="h-4 w-4 mr-2" /> Revenue
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <PieChart className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium">Finance Overview</h3>
              <p>View detailed financial summaries and reports in the other tabs.</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invoices" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table data-testid="invoices-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Bill To</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Paid</TableHead>
                    <TableHead>Balance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground" data-testid="no-invoices">
                        <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No invoices found</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    invoices.map((inv) => (
                      <TableRow key={inv.id} data-testid={`invoice-${inv.id}`}>
                        <TableCell className="font-mono">{inv.invoice_number}</TableCell>
                        <TableCell>{inv.bill_to_name}</TableCell>
                        <TableCell>{inv.invoice_date}</TableCell>
                        <TableCell>ETB {(inv.total_amount || 0).toLocaleString()}</TableCell>
                        <TableCell>ETB {(inv.paid_amount || 0).toLocaleString()}</TableCell>
                        <TableCell className={cn(inv.balance_due > 0 && 'text-destructive font-medium')}>
                          ETB {(inv.balance_due || 0).toLocaleString()}
                        </TableCell>
                        <TableCell>{getStatusBadge(inv.status)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {inv.status === 'DRAFT' && (
                              <Button size="sm" variant="outline" onClick={() => handleIssueInvoice(inv.id)}>
                                <Send className="h-4 w-4" />
                              </Button>
                            )}
                            {inv.status !== 'PAID' && inv.status !== 'CANCELLED' && inv.status !== 'DRAFT' && (
                              <Button size="sm" variant="outline" onClick={() => { setSelectedInvoice(inv); setPaymentDialogOpen(true); }}>
                                <CreditCard className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ar" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Accounts Receivable by Company</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table data-testid="ar-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Total Invoices</TableHead>
                    <TableHead>Open Invoices</TableHead>
                    <TableHead>Outstanding Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {arSummary.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                        <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No AR accounts found</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    arSummary.map((ar) => (
                      <TableRow key={ar.company_id}>
                        <TableCell className="font-medium">{ar.company_name}</TableCell>
                        <TableCell>{ar.total_invoices}</TableCell>
                        <TableCell>{ar.open_invoices}</TableCell>
                        <TableCell className={cn(ar.total_outstanding > 0 && 'text-destructive font-medium')}>
                          ETB {(ar.total_outstanding || 0).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="revenue" className="mt-4">
          <RevenueSummary propertyId={currentProperty?.id} />
        </TabsContent>
      </Tabs>

      {/* New Invoice Dialog */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Invoice</DialogTitle>
          </DialogHeader>
          <InvoiceForm 
            propertyId={currentProperty?.id}
            onSuccess={() => { setInvoiceDialogOpen(false); fetchInvoices(); }}
            onCancel={() => setInvoiceDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>Invoice {selectedInvoice?.invoice_number}</DialogDescription>
          </DialogHeader>
          <PaymentForm 
            invoice={selectedInvoice}
            onSubmit={handleRecordPayment}
            onCancel={() => { setPaymentDialogOpen(false); setSelectedInvoice(null); }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RevenueSummary({ propertyId }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    from: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    to: format(new Date(), 'yyyy-MM-dd'),
  });

  useEffect(() => {
    if (propertyId) {
      setLoading(true);
      financeApi.getRevenueSummary(propertyId, dateRange.from, dateRange.to)
        .then(res => setSummary(res.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [propertyId, dateRange]);

  if (loading) {
    return <Card><CardContent className="py-8"><Skeleton className="h-48" /></CardContent></Card>;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Revenue Summary</CardTitle>
          <div className="flex gap-2">
            <Input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
              className="w-[150px]"
            />
            <Input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
              className="w-[150px]"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {summary ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-700">ETB {(summary.total_revenue || 0).toLocaleString()}</div>
              <div className="text-sm text-green-600">Total Revenue</div>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-700">ETB {(summary.room_revenue || 0).toLocaleString()}</div>
              <div className="text-sm text-blue-600">Room Revenue</div>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-700">{summary.total_rooms_sold || 0}</div>
              <div className="text-sm text-purple-600">Rooms Sold</div>
            </div>
            <div className="p-4 bg-amber-50 rounded-lg">
              <div className="text-2xl font-bold text-amber-700">ETB {(summary.avg_adr || 0).toLocaleString()}</div>
              <div className="text-sm text-amber-600">Avg Daily Rate</div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No revenue data for selected period</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function InvoiceForm({ propertyId, onSuccess, onCancel }) {
  const [companies, setCompanies] = useState([]);
  const [formData, setFormData] = useState({
    bill_to_name: '',
    bill_to_email: '',
    company_id: '',
    notes: '',
    lines: [{ description: '', quantity: 1, unit_price: '', tax_rate: 15 }],
  });

  useEffect(() => {
    guestsApi.list({ profile_type: 'COMPANY' }).then(res => {
      setCompanies(res.data || []);
    });
  }, []);

  const addLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { description: '', quantity: 1, unit_price: '', tax_rate: 15 }]
    });
  };

  const updateLine = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index][field] = value;
    setFormData({ ...formData, lines: newLines });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        lines: formData.lines.map(l => ({
          ...l,
          quantity: parseFloat(l.quantity),
          unit_price: parseFloat(l.unit_price),
          tax_rate: parseFloat(l.tax_rate || 0),
        })),
      };
      await financeApi.createInvoice(propertyId, data);
      toast.success('Invoice created');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const handleCompanySelect = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    setFormData({
      ...formData,
      company_id: companyId,
      bill_to_name: company?.company_name || '',
      bill_to_email: company?.email || '',
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="invoice-form">
      <div className="space-y-2">
        <Label>Company (optional)</Label>
        <Select value={formData.company_id || 'none'} onValueChange={(v) => v !== 'none' && handleCompanySelect(v)}>
          <SelectTrigger><SelectValue placeholder="Select company" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No company (direct invoice)</SelectItem>
            {companies.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Bill To Name *</Label>
          <Input
            value={formData.bill_to_name}
            onChange={(e) => setFormData({ ...formData, bill_to_name: e.target.value })}
            required
          />
        </div>
        <div className="space-y-2">
          <Label>Email</Label>
          <Input
            type="email"
            value={formData.bill_to_email}
            onChange={(e) => setFormData({ ...formData, bill_to_email: e.target.value })}
          />
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Line Items</Label>
          <Button type="button" variant="outline" size="sm" onClick={addLine}>
            <Plus className="h-4 w-4 mr-1" /> Add Line
          </Button>
        </div>
        {formData.lines.map((line, i) => (
          <div key={i} className="grid grid-cols-4 gap-2">
            <Input
              placeholder="Description"
              value={line.description}
              onChange={(e) => updateLine(i, 'description', e.target.value)}
              className="col-span-2"
            />
            <Input
              type="number"
              placeholder="Qty"
              value={line.quantity}
              onChange={(e) => updateLine(i, 'quantity', e.target.value)}
              min="1"
            />
            <Input
              type="number"
              placeholder="Unit Price"
              value={line.unit_price}
              onChange={(e) => updateLine(i, 'unit_price', e.target.value)}
              min="0"
              step="0.01"
            />
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" data-testid="invoice-submit-btn">Create Invoice</Button>
      </div>
    </form>
  );
}

function PaymentForm({ invoice, onSubmit, onCancel }) {
  const [formData, setFormData] = useState({
    amount: invoice?.balance_due || '',
    payment_method: 'CASH',
    reference_number: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      amount: parseFloat(formData.amount),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="payment-form">
      <div className="p-4 bg-muted rounded-lg">
        <div className="flex justify-between text-sm">
          <span>Invoice Total:</span>
          <span>ETB {(invoice?.total_amount || 0).toLocaleString()}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span>Already Paid:</span>
          <span>ETB {(invoice?.paid_amount || 0).toLocaleString()}</span>
        </div>
        <div className="flex justify-between font-medium">
          <span>Balance Due:</span>
          <span className="text-destructive">ETB {(invoice?.balance_due || 0).toLocaleString()}</span>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Amount *</Label>
        <Input
          type="number"
          value={formData.amount}
          onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
          required
          min="0.01"
          step="0.01"
        />
      </div>
      <div className="space-y-2">
        <Label>Payment Method *</Label>
        <Select value={formData.payment_method} onValueChange={(v) => setFormData({ ...formData, payment_method: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="CASH">Cash</SelectItem>
            <SelectItem value="CREDIT_CARD">Credit Card</SelectItem>
            <SelectItem value="BANK_TRANSFER">Bank Transfer</SelectItem>
            <SelectItem value="MOBILE_MONEY">Mobile Money</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Reference #</Label>
        <Input
          value={formData.reference_number}
          onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
          placeholder="Receipt/Transaction number"
        />
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" data-testid="payment-submit-btn">Record Payment</Button>
      </div>
    </form>
  );
}
